import React, { useState, useMemo, useCallback } from 'react';
import { useWallet } from '../../contexts/WalletContext';
import { useToast } from '../../contexts/ToastContext';
import { useTheme } from '../../contexts/ThemeContext';
import WalletCreator from './WalletCreator';
import WalletConnector from './WalletConnector';
import ExternalWalletConnector from './ExternalWalletConnector';
import PrivateKeyExport from './PrivateKeyExport';
import { CopyableAddress } from '../common/CopyButton';
import SafeBalanceDisplay from '../common/SafeBalanceDisplay';

// Memoized Wallet Card Component for Performance
const WalletCard = React.memo(({
  wallet,
  isActive,
  isDeletingWallet,
  isLoading,
  onSetActive,
  onExportKey,
  onDelete,
  formatDate
}) => (
  <div className={`wallet-card modern-card ${isActive ? 'active' : ''}`}>
    <div className="card-glow"></div>
    <div className="wallet-info modern-info">
      <div className="wallet-type modern-type">
        <span className={`type-badge modern-badge ${wallet.wallet_type}`}>
          {wallet.wallet_type === 'platform' ? '🏦' : '🔗'}
          {wallet.wallet_type === 'platform' ? 'Platform' : 'External'}
        </span>
      </div>
      <div className="wallet-address modern-address">
        <CopyableAddress
          address={wallet.wallet_address}
          truncate={true}
          truncateLength={8}
          className="address-value"
        />
      </div>
      <div className="wallet-created modern-created">
        {formatDate(wallet.created_at)}
      </div>
    </div>
    <div className="wallet-actions modern-actions">
      {!isActive && (
        <button
          className="set-active-button modern-action-button primary"
          onClick={() => onSetActive(wallet)}
          disabled={isLoading}
        >
          <div className="button-icon">✓</div>
          <span>Set Active</span>
        </button>
      )}
      {wallet.wallet_type === 'platform' && (
        <button
          className="export-private-key-button modern-action-button danger"
          onClick={() => onExportKey(wallet)}
          disabled={isLoading}
          title="Export private key"
        >
          <div className="button-icon">🔑</div>
        </button>
      )}
      <button
        className="delete-button modern-action-button danger"
        onClick={() => onDelete(wallet.id)}
        disabled={isDeletingWallet === wallet.id || isLoading}
        title="Delete wallet"
      >
        <div className="button-icon">
          {isDeletingWallet === wallet.id ? '⏳' : '🗑️'}
        </div>
      </button>
    </div>
  </div>
));

WalletCard.displayName = 'WalletCard';

const WalletManager = () => {
  const {
    wallets,
    activeWallet,
    isLoading,
    error,
    setActiveWallet,
    deleteWallet,
    sendSOL,
    getWalletTransactions,
    checkForDeposits,
    clearError
  } = useWallet();

  const { showSuccess, showError, showWarning, showConfirmation, showInfo } = useToast();
  const { theme, isDark } = useTheme();

  const [showCreator, setShowCreator] = useState(false);
  const [showConnector, setShowConnector] = useState(false);
  const [showExternalConnector, setShowExternalConnector] = useState(false);
  const [deletingWallet, setDeletingWallet] = useState(null);
  const [showSendForm, setShowSendForm] = useState(false);
  const [sendForm, setSendForm] = useState({ recipient: '', amount: '' });
  const [transactions, setTransactions] = useState([]);
  const [showTransactions, setShowTransactions] = useState(false);
  const [showPrivateKeyExport, setShowPrivateKeyExport] = useState(false);
  const [walletToExport, setWalletToExport] = useState(null);

  // Memoized callback functions to prevent unnecessary re-renders
  const handleDeleteWallet = useCallback((walletId) => {
    showConfirmation(
      'Are you sure you want to delete this wallet? This action cannot be undone.',
      async () => {
        setDeletingWallet(walletId);
        const result = await deleteWallet(walletId);
        setDeletingWallet(null);

        if (result.success) {
          showSuccess('Wallet deleted successfully');
        } else {
          showError(`Failed to delete wallet: ${result.error}`);
        }
      },
      () => {
        // User cancelled - no action needed
      }
    );
  }, [deleteWallet, showConfirmation, showSuccess, showError]);

  const handleWalletCreated = useCallback((wallet) => {
    setShowCreator(false);
    // Success message could be shown here
  }, []);

  const handleWalletConnected = useCallback((wallet) => {
    setShowConnector(false);
    setShowExternalConnector(false);
    // Success message could be shown here
  }, []);

  const formatAddress = useCallback((address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-6)}`;
  }, []);

  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  const handleSendSOL = useCallback(async (e) => {
    e.preventDefault();
    if (!activeWallet || !sendForm.recipient || !sendForm.amount) return;

    const result = await sendSOL(activeWallet.id, sendForm.recipient, sendForm.amount);
    if (result.success) {
      showSuccess('Transaction sent successfully!');
      setSendForm({ recipient: '', amount: '' });
      setShowSendForm(false);
    } else {
      showError(`Transaction failed: ${result.error}`);
    }
  }, [activeWallet, sendForm, sendSOL, showSuccess, showError]);

  const handleGetTransactions = useCallback(async () => {
    if (!activeWallet) return;

    const result = await getWalletTransactions(activeWallet.id);
    if (result.success) {
      setTransactions(result.transactions);
      setShowTransactions(true);
    } else {
      showError(`Failed to load transactions: ${result.error}`);
    }
  }, [activeWallet, getWalletTransactions, showError]);

  const handleCheckDeposits = useCallback(async () => {
    if (!activeWallet) return;

    const result = await checkForDeposits(activeWallet.id);
    if (result.success) {
      if (result.deposits.length > 0) {
        showSuccess(`Found ${result.deposits.length} new deposits!`);
      } else {
        showInfo('No new deposits found');
      }
    } else {
      showError(`Failed to check deposits: ${result.error}`);
    }
  }, [activeWallet, checkForDeposits, showSuccess, showInfo, showError]);

  const handleExportPrivateKey = useCallback((wallet) => {
    if (wallet.wallet_type !== 'platform') {
      showError('Can only export private keys for platform wallets');
      return;
    }

    setWalletToExport(wallet);
    setShowPrivateKeyExport(true);
  }, [showError]);

  const handleClosePrivateKeyExport = useCallback(() => {
    setShowPrivateKeyExport(false);
    setWalletToExport(null);
  }, []);

  const handleSetActiveWallet = useCallback((wallet) => {
    setActiveWallet(wallet);
  }, [setActiveWallet]);

  // Memoize wallet grid rendering for performance with large wallet lists
  const walletGridMemo = useMemo(() => (
    <div className="wallets-grid modern-grid">
      {wallets.map((wallet) => (
        <WalletCard
          key={wallet.id}
          wallet={wallet}
          isActive={activeWallet?.id === wallet.id}
          isDeletingWallet={deletingWallet}
          isLoading={isLoading}
          onSetActive={handleSetActiveWallet}
          onExportKey={handleExportPrivateKey}
          onDelete={handleDeleteWallet}
          formatDate={formatDate}
        />
      ))}
    </div>
  ), [wallets, activeWallet?.id, deletingWallet, isLoading, handleSetActiveWallet, handleExportPrivateKey, handleDeleteWallet, formatDate]);


  if (showCreator) {
    return (
      <WalletCreator
        onClose={() => setShowCreator(false)}
        onSuccess={handleWalletCreated}
      />
    );
  }

  if (showConnector) {
    return (
      <WalletConnector
        onClose={() => setShowConnector(false)}
        onSuccess={handleWalletConnected}
      />
    );
  }

  if (showExternalConnector) {
    return (
      <div className="modal-overlay">
        <ExternalWalletConnector
          onClose={() => setShowExternalConnector(false)}
          onSuccess={handleWalletConnected}
        />
      </div>
    );
  }

  return (
    <div className="wallet-manager modern-wallet">
      <div className="wallet-manager-header modern-header">
        <div className="header-background"></div>
        <div className="header-content">
          <div className="header-title">
            <div className="title-icon">💼</div>
            <h1>Wallet Management</h1>
            <div className="title-subtitle">Secure Solana Wallet Management</div>
          </div>
        </div>
        <div className="header-actions modern-actions">
          <button
            className="create-wallet-button modern-button primary"
            onClick={() => setShowCreator(true)}
            disabled={isLoading}
          >
            <div className="button-icon">🏦</div>
            <span>Create Platform Wallet</span>
          </button>
          <button
            className="connect-wallet-button modern-button secondary"
            onClick={() => setShowExternalConnector(true)}
            disabled={isLoading}
            style={{ display: 'none' }}
          >
            <div className="button-icon">🔗</div>
            <span>Connect External Wallet</span>
          </button>
          <button
            className="manual-connect-button modern-button tertiary"
            onClick={() => setShowConnector(true)}
            disabled={isLoading}
            style={{ display: 'none' }}
          >
            <div className="button-icon">⚙️</div>
            <span>Manual Connect</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message modern-error">
          <div className="error-content">
            <span className="error-icon">⚠️</span>
            <span className="error-text">{error}</span>
          </div>
          <button className="error-close modern-close" onClick={clearError}>×</button>
        </div>
      )}

      {isLoading ? (
        <div className="loading-state modern-loading">
          <div className="loading-spinner modern-spinner">
            <div className="spinner-ring"></div>

          </div>
          <div className="loading-text">Loading your wallets...</div>
        </div>
      ) : wallets.length === 0 ? (
        <div className="empty-state modern-empty">
          <div className="empty-icon">👛</div>
          <h3>No Wallets Connected</h3>
          <p>Create a new platform wallet or connect your existing Solana wallet to start trading.</p>
          <div className="empty-actions modern-empty-actions">
            <button
              className="create-wallet-button modern-button primary"
              onClick={() => setShowCreator(true)}
            >
              <div className="button-icon">🏦</div>
              <span>Create Platform Wallet</span>
            </button>
            <button
              className="connect-wallet-button modern-button secondary"
              onClick={() => setShowExternalConnector(true)}
            >
              <div className="button-icon">🔗</div>
              <span>Connect External Wallet</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="wallets-list modern-list">
          <div className="active-wallet-section modern-section" >
            <div className="section-header" style={{ display: 'none' }}>
              <h3>Active Wallet</h3>
              <div className="section-subtitle">Currently selected wallet for transactions</div>
            </div>
            {activeWallet ? (
              <div className="wallet-card modern-card active">
                <div className="card-glow"></div>
                <div className="wallet-info modern-info">
                  <div className="wallet-type modern-type">
                    <span className={`type-badge modern-badge ${activeWallet.wallet_type}`}>
                      {activeWallet.wallet_type === 'platform' ? '🏦' : '🔗'}
                      {activeWallet.wallet_type === 'platform' ? 'Platform' : 'External'}
                    </span>
                  </div>
                  <div className="wallet-address modern-address">
                    <span className="address-label">Address:</span>
                    <CopyableAddress
                      address={activeWallet.wallet_address}
                      truncate={true}
                      truncateLength={8}
                      className="address-value"
                    />
                  </div>
                  <SafeBalanceDisplay
                    walletId={activeWallet.id}
                    compact={false}
                    className="wallet-balance-display modern-balance"
                  />
                  <div className="wallet-created modern-created">
                    Created: {formatDate(activeWallet.created_at)}
                  </div>
                </div>
                <div className="wallet-actions modern-actions">
                  <span className="active-badge modern-active-badge">Active</span>
                  <div className="action-buttons modern-action-buttons">
                    {activeWallet.can_trade && (
                      <button
                        className="send-button modern-action-button primary"
                        onClick={() => setShowSendForm(true)}
                        disabled={isLoading}
                      >
                        <div className="button-icon">💸</div>
                        <span>Send SOL</span>
                      </button>
                    )}
                    <button
                      className="transactions-button modern-action-button secondary"
                      onClick={handleGetTransactions}
                      disabled={isLoading}
                    >
                      <div className="button-icon">📊</div>
                      <span>Transactions</span>
                    </button>
                    <button
                      className="deposits-button modern-action-button tertiary"
                      onClick={handleCheckDeposits}
                      disabled={isLoading}
                    >
                      <div className="button-icon">🔍</div>
                      <span>Check Deposits</span>
                    </button>
                    {activeWallet.wallet_type === 'platform' && (
                      <button
                        className="export-private-key-button modern-action-button danger"
                        onClick={() => handleExportPrivateKey(activeWallet)}
                        disabled={isLoading}
                        title="Export private key for Phantom wallet"
                      >
                        <div className="button-icon">🔑</div>
                        <span>Export Key</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="no-active-wallet modern-no-active">
                <div className="no-active-icon">⚠️</div>
                <p>No active wallet selected</p>
              </div>
            )}
          </div>

          <div className="all-wallets-section modern-section">
            <div className="section-header">
              <h3>All Wallets ({wallets.length})</h3>
              <div className="section-subtitle">Manage all your connected wallets</div>
            </div>
            {walletGridMemo}
          </div>
        </div>
      )}

      {/* Send SOL Modal */}
      {showSendForm && (
        <div className="modal-overlay modern-modal-overlay">
          <div className="modal-content modern-modal">
            <div className="modal-header modern-header">
              <div className="modal-title">
                <div className="title-icon">💸</div>
                <h3>Send SOL</h3>
              </div>
              <button
                className="modal-close modern-close"
                onClick={() => setShowSendForm(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSendSOL} className="send-form modern-form">
              <div className="form-group modern-group">
                <label className="modern-label">Recipient Address</label>
                <input
                  type="text"
                  value={sendForm.recipient}
                  onChange={(e) => setSendForm({ ...sendForm, recipient: e.target.value })}
                  placeholder="Enter Solana address"
                  className="modern-input"
                  required
                />
              </div>
              <div className="form-group modern-group">
                <label className="modern-label">Amount (SOL)</label>
                <input
                  type="number"
                  step="0.000001"
                  value={sendForm.amount}
                  onChange={(e) => setSendForm({ ...sendForm, amount: e.target.value })}
                  placeholder="0.0"
                  className="modern-input"
                  required
                />
              </div>
              <div className="form-actions modern-actions">
                <button type="button" className="modern-button secondary" onClick={() => setShowSendForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="modern-button primary" disabled={isLoading}>
                  <div className="button-icon">💸</div>
                  <span>Send SOL</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Transactions Modal */}
      {showTransactions && (
        <div className="modal-overlay modern-modal-overlay">
          <div className="modal-content modern-modal large">
            <div className="modal-header modern-header">
              <div className="modal-title">
                <div className="title-icon">📊</div>
                <h3>Transaction History</h3>
              </div>
              <button
                className="modal-close modern-close"
                onClick={() => setShowTransactions(false)}
              >
                ×
              </button>
            </div>
            <div className="transactions-list modern-list">
              {transactions.length > 0 ? (
                transactions.map((tx, index) => (
                  <div key={index} className="transaction-item modern-item">
                    <div className="transaction-info modern-info">
                      <div className="transaction-signature modern-signature">
                        <span className="signature-label">Signature:</span>
                        <span className="signature-value">{formatAddress(tx.signature)}</span>
                        <button
                          className="copy-button modern-copy"
                          onClick={() => navigator.clipboard.writeText(tx.signature)}
                        >
                          📋
                        </button>
                      </div>
                      <div className="transaction-date modern-date">
                        {new Date(tx.timestamp * 1000).toLocaleString()}
                      </div>
                    </div>
                    <div className="transaction-amount modern-amount">
                      <span className={`amount modern-amount-value ${tx.amount > 0 ? 'positive' : 'negative'}`}>
                        {tx.amount > 0 ? '+' : ''}{tx.amount} SOL
                      </span>
                      <span className="transaction-status modern-status">{tx.status}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-transactions modern-empty">
                  <div className="empty-icon">📊</div>
                  <p>No transactions found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Private Key Export Modal */}
      {showPrivateKeyExport && walletToExport && (
        <PrivateKeyExport
          wallet={walletToExport}
          onClose={handleClosePrivateKeyExport}
        />
      )}
    </div>
  );
};

export default WalletManager;